/**
 * The reviews queue and one review's decision (`MCK-20260521-007`, C5/C6).
 *
 * Three copy decisions carry weight, all about finality and all stated before the act:
 *
 * `decide.final` is printed above the buttons, not inside the confirmation — somebody deciding
 * a review should know it cannot be edited *while* they choose, not after they have chosen.
 *
 * `target.superseded` says a newer version exists and that the decision still applies to the
 * sheet under review. Both halves matter: hiding the first would let somebody approve blind,
 * and omitting the second would read as a refusal.
 *
 * `decision.approveWithComments` keeps the messy middle a real option. Forcing approve-or-
 * reject pushes an architect into rubber-stamping or into issuing a whole version for a
 * dimension note.
 */

export const reviewsEn = {
  // The table and its rail (`KLQ-20260825-002` §3). A group count is about the pages that
  // have been loaded, never about the floor — hence the two spellings of it.
  'table.what': 'Under review',
  'table.who': 'Reviewer',
  'table.when': 'Due',
  'table.state': 'State',
  'axis.legend': 'Group by',
  'axis.location': 'Location',
  'axis.object': 'Object',
  'axis.phase': 'Phase',
  'axis.status': 'State',
  'axis.state.open': 'Awaiting a decision',
  'axis.state.decided': 'Decided',
  'axis.state.cancelled': 'Withdrawn',
  'axis.noObject': 'No object',
  'axis.noPhase': 'No phase',
  'axis.noState': 'No state',
  'table.flat': 'All reviews',
  'table.count': '{count, plural, one {# review} other {# reviews}}',
  'table.countPartial': '{count, plural, one {# review} other {# reviews}} so far',
  'table.unplaced': 'Elsewhere',
  'table.noTarget': 'no drawing',
  'table.andMore': '+{count, number} more',
  'list.partial': 'Counts cover the reviews loaded so far.',

  'slicer.title': 'Axis',
  // `slicer.*`, keyed by the **wire field name** the descriptor sends, because the rail looks
  // its label up rather than being handed one. Not `axis.*` — that group is the pivot's, and a
  // slice and a pivot are different operations over the same rows.
  'slicer.under': 'Location',
  'slicer.objectId': 'Object',
  'slicer.noValues': 'Nothing to slice by here.',
  'slicer.unknownValue': 'something this list cannot name',
  'under.all': 'Whole project',

  'detail.close': 'Close',

  'page.title': 'Reviews',
  'page.subtitle': 'Who is waiting on whom, and what has been signed off.',

  'tab.awaiting_me': 'Awaiting me',
  'tab.awaiting_others': 'Awaiting others',
  'tab.closed': 'Closed',
  'tab.all': 'All',

  'status.loading': 'Loading reviews…',
  'status.explains': 'A review asks someone to approve or comment on a drawing.',
  'status.emptyTitle': 'No reviews',
  'status.empty': 'Nothing here.',
  'status.emptyAwaitingMeTitle': 'Nothing for you to decide',
  'status.emptyAwaitingMe': 'Nothing is waiting on you.',
  'list.more': 'Show older',
  'list.loadingMore': 'Loading…',

  'row.due': 'Due {when}',
  'row.overdue': 'Overdue',
  'row.drawings': '{count, plural, one {# drawing} other {# drawings}}',
  'row.awaitsYou': 'Waiting on you',

  'review.title': 'Review',
  'review.requestedBy': 'Requested by',
  'review.reviewer': 'Reviewer',
  'review.requested': 'Requested',
  'review.due': 'Due',
  'review.note': 'Note from the requester',
  'review.decidedAt': 'Decided',
  'review.decisionNote': 'Reviewer’s note',
  'review.cancelled': 'This review was withdrawn.',
  'review.targets': 'Drawings under review',
  'review.pick': 'Pick a review to see it.',

  'target.superseded':
    'A newer version has been published since this review was requested. Your decision applies to the version you are reviewing, not to the newer one.',
  'target.reviewing': 'Reviewing',
  'target.current': 'Current now',

  'decide.title': 'Your decision',
  'decide.final':
    'A decision is final. To change one, the requester publishes the next version and asks again.',
  'decide.note': 'Note (optional)',
  'decide.notePlaceholder': 'Fix the dimension on the north elevation…',
  'decide.confirm': 'This is final. Record it?',
  'decide.confirmYes': 'Yes, record the decision',
  'decide.confirmNo': 'Not yet',

  'decision.approve': 'Approve',
  'decision.approve_with_comments': 'Approve with comments',
  'decision.request_changes': 'Request changes',
  'decision.made.approve': 'Approved',
  'decision.made.approve_with_comments': 'Approved with comments',
  'decision.made.request_changes': 'Changes requested',

  'cancel.action': 'Withdraw this review',
  'cancel.confirm': 'Yes, withdraw it',
  'cancel.keep': 'Keep it open',
  'cancel.help': 'Withdrawing it asks nobody for anything. It stays on the record.',

  'receipt.action': 'Show the record',
  'receipt.title': 'Record of decision',
  'receipt.decidedAt': 'Decided at',
  'receipt.by': 'By',
  'receipt.company': 'Company',
  'receipt.print': 'Print / Save as PDF',
  'receipt.decision': 'Decision',
  'receipt.role': 'Role at the time',
  'receipt.targets': 'Drawing · version',
  'receipt.note': 'Decision note',
  'receipt.id': 'Record',
  'receipt.proof': 'Proof',
  'receipt.key': 'Signing key',
  'receipt.help':
    'This record is part of the project’s hash chain, so it can be verified rather than believed.',

  'request.action': 'Request a review',
  'request.title': 'Request a review',
  'request.intro':
    'Each drawing is pinned to the version that is current right now. A publish afterwards does not move the review.',
  'request.reviewer': 'Reviewer',
  'request.reviewerEmpty': 'Nobody on this project can be asked yet.',
  'request.drawings': 'Drawings',
  'request.drawingsEmpty': 'This project has no drawings yet.',
  'request.due': 'Due date',
  'request.note': 'What should they look at? (optional)',
  'request.submit': 'Send the request',
  'request.cancel': 'Cancel',

  'error.forbidden': 'You are not allowed to do that on this review.',
  'error.gone': 'That review is no longer here.',
  'error.final':
    'This review has already been decided or withdrawn. The next version gets its own review.',
  'error.validation': 'That request was refused. Check the fields and try again.',
  'error.rateLimited': 'Too many attempts. Wait a moment and try again.',
  'error.cursor': 'That list moved on. Reload it to see the latest.',
  'error.unexpected': 'Something went wrong. Try again.',
} as const;

export type ReviewsMessageKey = keyof typeof reviewsEn;
